import { AxiosError, AxiosResponse } from 'axios';
import { ActionContext } from 'vuex';
import services from '@/app/services/projects-map';

export default {
  fetchProjectsByAToZ(context: ActionContext<ProjectsState, RootState>) {
    return services
      .projectsByAToZ()
      .then((response: AxiosResponse) => {
        if (response.data.success === true) {
          context.commit(
            'SET_PROJECTS_LIST',
            response.data.projects.map((project: ProjectItemAlt) => {
              return {
                region: project.alphanumeric,
                locations: project.projectCollection,
                id: project.id,
                sortBy: 'name',
                sortOrder: 'ascending',
              };
            }),
          );
          context.commit('SET_LIST_ERROR', '');
          context.commit('SET_LIST_VIEW_IS_A_TO_Z', true);
          context.commit('SET_LAST_SEARCHED_VALUE', '');
          context.commit('SET_SUGGESTIONS_MODEL', '');
        }
      })
      .catch((error: AxiosError) => {
        throw error;
      });
  },
  fetchProjectsByLocation(context: ActionContext<ProjectsState, RootState>) {
    return services
      .projectsByLocation()
      .then((response: AxiosResponse) => {
        if (response.data.success === true) {
          context.commit(
            'SET_PROJECTS_LIST',
            response.data.projects.map((project: ProjectItem) => {
              return {
                ...project,
                sortBy: 'name',
                sortOrder: 'ascending',
              };
            }),
          );
          context.commit('SET_LIST_VIEW_IS_A_TO_Z', false);
        }
      })
      .catch((error: AxiosError) => {
        throw error;
      });
  },
  fetchProjectsByName(context: ActionContext<ProjectsState, RootState>) {
    const searchTerm = context.state.selectedLocation.magicKey
      ? context.state.selectedLocation.text
      : context.state.suggestionsModel;

    if (searchTerm) {
      context.commit('SET_LAST_SEARCHED_VALUE', searchTerm);
      return services
        .projectsByName(searchTerm)
        .then((response: AxiosResponse) => {
          if (response.data.success === true) {
            context.commit(
              'SET_PROJECTS_LIST',
              response.data.projects.map((project: ProjectItem) => {
                return {
                  ...project,
                  sortBy: 'name',
                  sortOrder: 'ascending',
                };
              }),
            );
            context.commit('SET_LIST_ERROR', '');
          } else {
            throw new Error(`No projects matching "${searchTerm}"`);
          }
        })
        .catch((error: AxiosError) => {
          throw error;
        });
    }
    return;
  },
  fetchSearchResults(context: ActionContext<ProjectsState, RootState>) {
    if (context.state.selectedLocation.text) {
      return services
        .search(context.state.selectedLocation.magicKey)
        .then((response: AxiosResponse) => {
          history.replaceState(
            {},
            '',
            `${location.pathname}?searchTerm=${context.state.selectedLocation.text}&mk=${context.state.selectedLocation.magicKey}`,
          );
          context.commit('SET_LOCATION_COORDINATES', response.data.candidates[0].location);
          context.dispatch('resetSuggestions');
        })
        .catch((error: AxiosError) => {
          throw error;
        });
    }
    return;
  },
  fetchSelectedFeatureSummary(
    context: ActionContext<ProjectsState, RootState>,
    selectedFeatureId: string,
  ) {
    return services
      .selectedFeatureSummary(selectedFeatureId)
      .then((response: AxiosResponse) => {
        if (response.data.success === true) {
          if (context.state.userGuideVisible === true) {
            context.commit('SET_USER_GUIDE_VISIBILITY', false);
          }
          context.commit('SET_SELECTED_FEATURE_SUMMARY', response.data.summary);
        } else {
          throw new Error('Feature summary was unable to load');
        }
      })
      .catch((error: AxiosError) => {
        throw error;
      });
  },
  fetchSuggestions(context: ActionContext<ProjectsState, RootState>, suggestionsValue: string) {
    return services
      .suggestions(suggestionsValue)
      .then((response: AxiosResponse) => {
        context.commit('SET_SUGGESTED_LOCATIONS', response.data.locations);
        context.commit('SET_SUGGESTED_PROJECTS', response.data.projects);
      })
      .catch((error: AxiosError) => {
        throw error;
      });
  },
  async filterSuggestions(
    context: ActionContext<ProjectsState, RootState>,
    suggestionsValue: string,
  ) {
    const suggestions = await context.state.projectsList.filter((value) =>
      value.region.toLowerCase().includes(suggestionsValue.toLowerCase()),
    );
    context.commit(
      'SET_SUGGESTED_LOCATIONS',
      suggestions.map((value) => {
        return {
          text: value.region,
        };
      }),
    );
  },
  async querySearchParams(context: ActionContext<ProjectsState, RootState>) {
    const searchParams = await new URLSearchParams(window.location.search);
    context.commit('SET_SELECTED_LOCATION', {
      isCollection: false,
      magicKey: searchParams.get('mk'),
      text: searchParams.get('searchTerm'),
    });

    if (searchParams.get('view') === 'list') {
      context.dispatch('fetchProjectsByLocation');
    }
  },
  resetFilter(context: ActionContext<ProjectsState, RootState>) {
    context.commit('SET_PROJECTS_LIST_FILTER', 'projectsLocation');
    context.commit('SET_LIST_VIEW_IS_A_TO_Z', false);
  },
  resetList(context: ActionContext<ProjectsState, RootState>, resetError: boolean) {
    context.dispatch('fetchProjectsByLocation').then(() => {
      context.dispatch('resetMap');
      context.commit('SET_SUGGESTIONS_MODEL', '');
      context.commit('SET_LAST_SEARCHED_VALUE', '');
      if (resetError === true) {
        context.commit('SET_LIST_ERROR', '');
      }
    });
  },
  resetMap(context: ActionContext<ProjectsState, RootState>) {
    context.commit('SET_LOCATION_COORDINATES', null);
    history.replaceState({}, '', location.pathname);
    context.dispatch('resetSelectedFeatureSummary');
    context.dispatch('resetSelectedLocation');
    context.dispatch('resetSuggestions');
    context.commit('SET_SUGGESTIONS_MODEL', '');
    context.commit('SET_LAST_SEARCHED_VALUE', '');
    context.commit('SET_LAYER_ERROR', '');
    context.dispatch('resetFilter');
  },
  resetSelectedFeatureSummary(context: ActionContext<ProjectsState, RootState>) {
    context.commit('SET_SELECTED_FEATURE_SUMMARY', {});
  },
  resetSelectedLocation(context: ActionContext<ProjectsState, RootState>) {
    context.commit('SET_SELECTED_LOCATION', {
      isCollection: false,
      magicKey: '',
      text: '',
    });
  },
  resetSuggestions(context: ActionContext<ProjectsState, RootState>) {
    context.commit('SET_SUGGESTED_LOCATIONS', []);
    context.commit('SET_SUGGESTED_PROJECTS', []);
  },
  setLocationValues(context: ActionContext<ProjectsState, RootState>) {
    if (context.getters.hasSelectedLocation === false && context.getters.hasSuggestions === true) {
      context.commit(`SET_SELECTED_LOCATION`, context.state.suggestedLocations[0]);
    }
    context.commit(
      'SET_LAST_SEARCHED_VALUE',
      context.state.selectedLocation.text || context.state.suggestionsModel,
    );
    context.commit('SET_SUGGESTIONS_MODEL', context.state.lastSearchedValue);
  },
  setSelectedFeatureSummary(
    context: ActionContext<ProjectsState, RootState>,
    selectedFeatureSummary: SelectedFeatureSummary,
  ) {
    if (context.state.userGuideVisible === true) {
      context.commit('SET_USER_GUIDE_VISIBILITY', false);
    }
    context.commit('SET_SELECTED_FEATURE_SUMMARY', selectedFeatureSummary);
  },
  sortProjectsTable(context: ActionContext<ProjectsState, RootState>, sortProps: SortProps) {
    context.commit('SET_SORT_BY', sortProps);
  },
  toggleUserGuideVisibility(context: ActionContext<ProjectsState, RootState>, value: boolean) {
    if (value === true && context.getters.hasSelectedFeature === true) {
      context.dispatch('resetSelectedFeatureSummary');
    }
    context.commit('SET_USER_GUIDE_VISIBILITY', value);
  },
};
